import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Image, Form } from "react-bootstrap";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { ShieldCheck, Heart, PenLine, ArrowLeft } from "lucide-react";
import { Button as DSButton } from "../../../components/DesignSystem/components/Button";
import { Heading, Text } from "../../../components/DesignSystem/components/Typography";

import "bootstrap/dist/css/bootstrap.min.css";
import "./EnterCode.css";

import responsivebg from "../../../assets/images/responsive-bg.png";
import backarrow from "../../../assets/images/backarrow.jpg";
import logo2 from "../../../assets/images/logo2.png";

import Stepper from "../../../components/SignUp/Stepper";
import { useCookies } from "../../../hooks/useCookies";
import { formatPhoneNumber } from "../../../utils/utils";
import * as verificationAction from "../../../actions/verification"

import profile1 from "../../../assets/images/profile1.png";
import profile2 from "../../../assets/images/profile2.png";
import profile3 from "../../../assets/images/profile3.png";
import imgEntercode from "../../../assets/images/img_entercode.png";

export const Entercode = () => {
  const { getCookie, setCookie } = useCookies();
  const [phoneNumber, setPhoneNumber] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const goToEmailVerify = () => {
    navigate("/emailverify");
  };

  // State for OTP code inputs
  const [code1, setCode1] = useState("");
  const [code2, setCode2] = useState("");
  const [code3, setCode3] = useState("");
  const [code4, setCode4] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Refs for input fields
  const code1ref = useRef(null);
  const code2ref = useRef(null);
  const code3ref = useRef(null);
  const code4ref = useRef(null);

  // Resend OTP timer state and handler
  const [resendTimer, setResendTimer] = useState(120);
  const [isResendDisabled, setIsResendDisabled] = useState(true); // Initially disabled

  useEffect(() => {
    // Get phone number from cookies
    const savedPhoneNumber = getCookie("phoneNumber");
    if (savedPhoneNumber) {
      setPhoneNumber(savedPhoneNumber);
    }

    // Countdown logic
    const interval = setInterval(() => {
      setResendTimer((prevTimer) => {
        if (prevTimer > 0) {
          return prevTimer - 1;
        } else {
          setIsResendDisabled(false); // Enable resend button when timer reaches 0
          clearInterval(interval);
          return 0;
        }
      });
    }, 1000);

    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, [getCookie, location.state]);

  // Function to handle OTP code input change
  const handleCodeChange = (e, setter, nextRef) => {
    const value = e.target.value;
    if (!isNaN(value) && value?.length <= 1) {
      setter(value);

      if (value?.length === 1 && nextRef) {
        nextRef.current.focus();
      }
    }
  };

  // Function to handle Backspace and focus on previous input
  const handleKeyDown = (e, prevRef) => {
    if (e.key === "Backspace" && e.target.value === "" && prevRef) {
      prevRef.current.focus();
    }
  };

  // Function to handle clipboard paste for OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    if (!/^\d+$/.test(pastedData)) return; // Ensure only numbers
    
    const chars = pastedData.split('');
    if (chars[0]) setCode1(chars[0]);
    if (chars[1]) setCode2(chars[1]);
    if (chars[2]) setCode3(chars[2]);
    if (chars[3]) setCode4(chars[3]);
    
    // Auto-focus logic based on paste length
    if (chars.length === 1) code2ref.current?.focus();
    else if (chars.length === 2) code3ref.current?.focus();
    else if (chars.length === 3) code4ref.current?.focus();
    else if (chars.length === 4) code4ref.current?.focus(); 
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    const otp = code1 + code2 + code3 + code4;
    if (otp?.length !== 4) {
      setErrorMessage("*4-digit code is required.");
    } else {
      setIsLoading(true);
      try {
        let body = {
          phone: phoneNumber,
          otp: otp,
        };
        const response = await verificationAction.signupCode(body);
        if (response?.userId) {
          setResendTimer(120);
          setErrorMessage("");
          setIsResendDisabled(true);
          setCookie("token", response.token, 30);
          setCookie("userId", response.userId, 30);
          
          // Micro-interaction: short delay before nav to feel smooth 
          setTimeout(() => {
              goToEmailVerify();
          }, 100);
        } else {
          setErrorMessage(response?.response?.data?.message || response?.message || "*Failed to send code");
        }
      } catch (error) {
        console.error("Error:", error);
        setErrorMessage("An error occurred. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Function to resend OTP
  const resendotp = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (resendTimer === 0) {
      try {
        let body = {
          phone: phoneNumber,
        };
        const response = await verificationAction.signupOtp(body);
        if (response) {
          setResendTimer(120); // Reset timer to 2 minutes on successful resend
          setIsResendDisabled(true); // Disable resend button after OTP is sent
        } else {
          setErrorMessage("*Failed to send OTP.");
        }
      } catch (error) {
        console.error("Error:", error);
        setErrorMessage("An error occurred. Please try again later.");
      }
    }
  };

  // Function to format remaining time in mm:ss format
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const theme = useTheme();
  const mode = theme.palette.mode;
  
  // Track Tailwind loading state to prevent FOUC (Flash of Unstyled Content)
  const [isTailwindLoaded, setIsTailwindLoaded] = useState(!!window.tailwind);

  useEffect(() => {
    const applyConfig = () => {
      const modeColors = tokens(mode);
      if (window.tailwind) {
        window.tailwind.config = {
          darkMode: "class",
          theme: {
              extend: {
                  colors: {
                      "primary": modeColors.greenAccent[500] || "#ff794d",
                      "background-light": mode === "dark" ? modeColors.primary[500] : "#f8f6f5",
                      "background-dark": modeColors.primary[500] || "#23140f",
                      "premium-black": mode === "dark" ? "#ffffff" : "#111111"
                  },
                  fontFamily: {
                      "display": ["Inter", "sans-serif"],
                      "body": ['"Source Sans Pro"', "sans-serif"],
                      "button": ["Poppins", "sans-serif"]
                  },
                  borderRadius: {
                      "DEFAULT": "1rem",
                      "lg": "2rem",
                      "xl": "3rem",
                      "full": "9999px"
                  },
              },
          },
        };
      }
    };

    if (!document.getElementById("tailwind-script")) {
      const script = document.createElement("script");
      script.id = "tailwind-script";
      script.src = "https://cdn.tailwindcss.com?plugins=forms,container-queries";
      
      script.onload = () => {
        applyConfig();
        setIsTailwindLoaded(true);
      };

      document.head.appendChild(script);
      
      const link1 = document.createElement("link");
      link1.rel = "stylesheet";
      link1.href = "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght@100..700,0..1&display=swap";
      document.head.appendChild(link1);
    } else {
      applyConfig();
      // Fast check if already loaded
      if (window.tailwind) setIsTailwindLoaded(true);
    }
  }, [mode]);

  return (
    <div 
      style={{ opacity: isTailwindLoaded ? 1 : 0, transition: 'opacity 0.1s ease-out' }}
      className="font-display text-slate-900 dark:text-slate-100 antialiased h-screen overflow-hidden"
    >
      <div className="flex h-screen w-full flex-col lg:flex-row overflow-hidden">
        {/* Left Side: Visual Anchor */}
        <div className="relative hidden lg:flex lg:w-1/2 flex-col justify-end p-[var(--ds-space-12)] pb-[var(--ds-space-16)] overflow-hidden h-full">
            <div className="absolute inset-0 z-0">
                <div className="w-full h-full bg-cover"
                    style={{ backgroundImage: `url(${imgEntercode})`, backgroundPosition: '30% center' }}>
                </div>
            </div>
            <div className="relative z-10 max-w-md text-white">
              
                <Heading variant="h2" weight="black" className="text-white/100 text-5xl tracking-tight mb-6 mt-0">Your safety is our priority.</Heading>
                <Text as="p" size="xl" className="text-white/90 leading-relaxed mb-0">We use two-factor authentication to ensure every
                    connection is genuine and secure.</Text>
                <div className="hero-avatars">
                            <div className="avatar-stack">
                                {/* Placeholders - Replace with your assets/images/lp1.png etc if desired */}
                                <img src={profile1}/>
                                <img src={profile2} style={{ marginLeft: '-15px' }}/>
                                <img src={profile3} style={{ marginLeft: '-15px' }}/>
                               <div className="flex items-center justify-center w-12 h-12 lp-rounded-full border-4 border-background-light lp-bg-primary text-white text-xs lp-font-bold" style={{ width: '3rem', height: '3rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '4px solid white', marginLeft: '-15px', zIndex: 10 }}>
                                    +40k
                                </div>
                            </div>
                            <p className="pl-6 lp-text-sm lp-font-medium lp-text-white-500" style={{ paddingLeft: '1.5rem' }}>Trusted by 40,000+ verified members.</p>
                        </div>
            </div>
        </div>

        {/* Right Side: OTP Verification Form */}
        <div className="w-full lg:w-1/2 flex flex-col bg-white dark:bg-background-dark px-6 lg:px-[var(--ds-space-20)] pt-[var(--ds-space-mobile-top)] pb-[var(--ds-space-12)] lg:py-[var(--ds-space-16)] h-full overflow-y-auto">
            <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
                
                {/* Content Wrapper for Responsive Vertical Centering */}
                <div className="flex-1 flex flex-col justify-start lg:justify-center w-full lg:my-auto pb-8 lg:py-8">
                    {/* Mobile Back Button */}
                    <div className="lg:hidden flex justify-start mb-[var(--ds-space-8)] -ml-2 text-slate-900 dark:text-slate-100">
                    <button type="button" onClick={() => window.history.back()} className="flex items-center justify-center w-10 h-10 rounded-full bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" aria-label="Go back">
                        <ArrowLeft className="shrink-0" size={24} />
                    </button>
                </div>

                <div className="mb-[var(--ds-space-8)]">
                    <h1 className="text-slate-900 dark:text-slate-100 text-4xl lg:text-5xl font-black font-display tracking-tight mb-4">
                        Enter your code
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 text-lg leading-normal">
                        We’ve sent a 4-digit code to <span className="font-semibold text-slate-900 dark:text-white">{formatPhoneNumber(phoneNumber)}</span>
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-[var(--ds-space-8)]" >
                    <div className="flex justify-between gap-4 lg:gap-6 px-4" style={{ alignItems: 'center' }}>
                        <input
                            ref={code1ref}
                            value={code1}
                            onChange={(e) => handleCodeChange(e, setCode1, code2ref)}
                            onKeyDown={(e) => handleKeyDown(e, null)}
                            onPaste={handlePaste}
                            className="otp-input w-full aspect-square text-center text-3xl font-bold bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-2xl focus:border-primary transition-all text-slate-900 dark:text-white"
                            maxLength="1" placeholder="•" type="number" 
                        />
                        <input
                            ref={code2ref}
                            value={code2}
                            onChange={(e) => handleCodeChange(e, setCode2, code3ref)}
                            onKeyDown={(e) => handleKeyDown(e, code1ref)}
                            className="otp-input w-full aspect-square text-center text-3xl font-bold bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-2xl focus:border-primary transition-all text-slate-900 dark:text-white"
                            maxLength="1" placeholder="•" type="number" 
                        />
                        <input
                            ref={code3ref}
                            value={code3}
                            onChange={(e) => handleCodeChange(e, setCode3, code4ref)}
                            onKeyDown={(e) => handleKeyDown(e, code2ref)}
                            className="otp-input w-full aspect-square text-center text-3xl font-bold bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-2xl focus:border-primary transition-all text-slate-900 dark:text-white"
                            maxLength="1" placeholder="•" type="number" 
                        />
                        <input
                            ref={code4ref}
                            value={code4}
                            onChange={(e) => handleCodeChange(e, setCode4, null)}
                            onKeyDown={(e) => handleKeyDown(e, code3ref)}
                            className="otp-input w-full aspect-square text-center text-3xl font-bold bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-2xl focus:border-primary transition-all text-slate-900 dark:text-white"
                            maxLength="1" placeholder="•" type="number" 
                        />
                    </div>
                    
                    {errorMessage && (
                        <Text as="div" size="sm" weight="medium" className="text-red-500 text-center -mt-[var(--ds-space-2)] mb-[var(--ds-space-2)]">
                            {errorMessage}
                        </Text>
                    )}

                    <div className="flex flex-col gap-[var(--ds-space-4)] mb-8">
                        <div className="flex flex-col items-center gap-[var(--ds-space-4)] text-sm font-medium mt-[var(--ds-space-6)]">
                            <DSButton 
                                variant="ghost"
                                className={`transition-colors text-center ${isResendDisabled ? 'text-slate-400 cursor-not-allowed' : 'text-primary hover:text-primary/80'}`} 
                                type="button"
                                onClick={resendotp}
                                disabled={isResendDisabled}
                            >
                                {isResendDisabled ? `Didn't receive a code? Resend in ${formatTime(resendTimer)}` : "Didn't receive a code? Resend"}
                            </DSButton>
                            <DSButton
                                variant="ghost"
                                className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors flex items-center gap-[var(--ds-space-2)]"
                                type="button"
                                onClick={() => window.history.back()}
                                startIcon={<PenLine size={16} />}
                            >
                                Change phone number
                            </DSButton>
                        </div>
                    </div>

                    <div className="max-lg:fixed max-lg:bottom-0 max-lg:left-0 max-lg:right-0 max-lg:bg-white max-lg:dark:bg-background-dark max-lg:p-6 max-lg:border-t max-lg:border-slate-100 dark:max-lg:border-slate-800 max-lg:z-50">
                        <DSButton
                            type="submit"
                            variant="cta"
                            style={{ backgroundColor: mode === "dark" ? "#ffffff" : "#111111", color: mode === "dark" ? "#111111" : "#ffffff" }}
                            disabled={isLoading}
                        >
                            {isLoading ? "Verifying..." : "Verify"}
                        </DSButton>
                    </div>
                </form>
                {/* Spacer to push content up on mobile so it's not hidden behind the fixed CTA */}
                <div className="lg:hidden h-28 shrink-0"></div> 
                </div>

                {/* Footer placed at extreme bottom via mt-auto pulling remaining flex space */}
                <div className="mt-auto pt-[var(--ds-space-8)] border-t border-slate-200 dark:border-slate-800 text-center opacity-70 grayscale">
                    <Text as="p" size="xs" weight="bold" className="text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em] text-center">
                        Secure Encryption
                    </Text>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};
